/**
 * Shared Payroll Register cover-page aggregation and PDF rendering.
 *
 * Excel receives the canonical summary from the backend. The browser-side
 * aggregator is retained as a rolling-deployment fallback for older API
 * responses so PDF exports never lose their approval cover page.
 */

import {
  ALIGN,
  DEFAULT_TABLE_STYLES,
  drawBrandedHeader,
  drawSeparatorLine,
  fmtCurrency,
} from './pdfReportTemplate';

const SIGNATORY_ROLES = [
  'Prepared & Checked By',
  'Verified By',
  'Approved By',
];

const COVER_BAND_STYLES = {
  ot: {
    header: [45, 106, 138],
    body: [184, 216, 232],
    total: [35, 78, 105],
  },
  cpf: {
    header: [30, 90, 138],
    body: [204, 229, 246],
    total: [22, 66, 102],
  },
  ded: {
    header: [139, 69, 83],
    body: [242, 184, 196],
    total: [100, 45, 60],
  },
};

function numberValue(value) {
  if (value == null || value === '') return 0;
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : 0;
}

function paymentSplit(rows, payableKey) {
  return rows.reduce((totals, row) => {
    const amount = numberValue(row[payableKey]);
    if ((row.run_type || '').toLowerCase() === 'final_settlement') {
      totals.paid_amount += amount;
    } else if (['giro', 'bank_transfer'].includes((row.pay_mode || '').toLowerCase())) {
      totals.giro += amount;
    } else {
      totals.cash_payment += amount;
    }
    return totals;
  }, { paid_amount: 0, cash_payment: 0, giro: 0 });
}

/**
 * Build a cover summary when the backend response predates cover_summary.
 *
 * @param {Array<Object>} rows Payroll register rows.
 * @param {Object} config Aggregation configuration.
 * @returns {Object} Cover summary matching the backend response contract.
 */
export function buildPayrollCoverSummary(rows, config) {
  const {
    groupKey,
    groupLabel,
    payableKey,
    metrics,
    groupOrder = [],
    groupLabels = {},
    includeEmptyGroups = false,
  } = config;

  const groups = new Map();
  rows.forEach((row) => {
    const group = String(row[groupKey] || 'Unassigned');
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(row);
  });

  const orderedGroups = groupOrder.length
    ? [...groupOrder, ...[...groups.keys()].filter((group) => !groupOrder.includes(group)).sort()]
    : [...groups.keys()].sort();

  const columns = [
    { key: 'sn', label: 'S/N', type: 'integer', band: null },
    { key: 'group', label: groupLabel, type: 'text' },
    { key: 'headcount', label: 'Headcount', type: 'integer', band: null },
    ...metrics.map((metric) => ({
      key: metric.key,
      label: metric.label,
      type: metric.type || 'currency',
      band: metric.band || null,
    })),
  ];

  const summaryRows = orderedGroups
    .map((group, index) => {
      const groupRows = groups.get(group) || [];
      if (!groupRows.length && !includeEmptyGroups) return null;

      const row = {
        sn: index + 1,
        group: groupLabels[group] || group,
        headcount: groupRows.length,
      };
      metrics.forEach((metric) => {
        const value = groupRows.reduce(
          (total, item) => total + numberValue(item[metric.sourceKey]),
          0,
        );
        row[metric.key] = metric.negate ? -value : value;
      });
      const split = paymentSplit(groupRows, payableKey);
      return {
        ...row,
        ...split,
        total_payable: split.paid_amount + split.cash_payment + split.giro,
      };
    })
    .filter(Boolean);

  summaryRows.forEach((row, index) => {
    row.sn = index + 1;
  });

  const totals = { sn: '', group: 'Grand Total', headcount: rows.length };
  columns.slice(3).forEach((column) => {
    totals[column.key] = summaryRows.reduce(
      (total, row) => total + numberValue(row[column.key]),
      0,
    );
  });
  const split = paymentSplit(rows, payableKey);
  Object.assign(totals, {
    ...split,
    total_payable: split.paid_amount + split.cash_payment + split.giro,
  });

  return {
    columns,
    rows: summaryRows,
    totals,
    already_paid_total: totals.paid_amount,
    current_release_total: totals.cash_payment + totals.giro,
    register_total: totals.total_payable,
  };
}

function formatCoverValue(column, value) {
  if (['currency', 'number'].includes(column.type)) {
    return numberValue(value).toLocaleString('en-SG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  if (column.type === 'rate') {
    return `${(numberValue(value) * 100).toFixed(2)}%`;
  }
  if (column.type === 'integer') {
    return value === '' ? '' : String(numberValue(value));
  }
  return String(value ?? '-');
}

/**
 * Convert a cover summary into jsPDF-AutoTable head/body arrays.
 *
 * @param {Object} summary Canonical cover summary.
 * @returns {{head: Array<Array<string>>, body: Array<Array<string>>, totalRowIndex: number}}
 */
export function buildCoverTableData(summary) {
  const columns = summary?.columns || [];
  const rows = summary?.rows || [];
  const body = rows.map((row) => (
    columns.map((column) => formatCoverValue(column, row[column.key]))
  ));
  body.push(
    columns.map((column) => formatCoverValue(column, summary?.totals?.[column.key])),
  );
  return {
    head: [columns.map((column) => column.label)],
    body,
    totalRowIndex: body.length - 1,
  };
}

function drawSignatures(doc, layout, startY) {
  const gap = 8;
  const blockWidth = (layout.contentWidth - (gap * 2)) / 3;

  SIGNATORY_ROLES.forEach((role, index) => {
    const startX = layout.leftX + index * (blockWidth + gap);
    const endX = startX + blockWidth;

    doc.setFillColor(232, 236, 244);
    doc.roundedRect(startX, startY, blockWidth, 8, 1.5, 1.5, 'F');

    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(27, 42, 74);
    doc.text(role, startX + blockWidth / 2, startY + 5.25, { align: 'center' });

    doc.setDrawColor(27, 42, 74);
    doc.setLineWidth(0.35);
    doc.line(startX, startY + 20, endX, startY + 20);

    doc.setFontSize(6.5);
    doc.setFont(undefined, 'italic');
    doc.setTextColor(107);
    doc.text('Signature', startX + blockWidth / 2, startY + 24, { align: 'center' });

    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(90);
    doc.text('Name', startX, startY + 33);
    doc.line(startX + 12, startY + 33, endX, startY + 33);
    doc.text('Date', startX, startY + 41);
    doc.line(startX + 12, startY + 41, endX, startY + 41);
  });
  doc.setTextColor(0);
}

function drawMetadataTile(doc, {
  x,
  y,
  width,
  label,
  value,
  fill,
  textColor,
}) {
  const height = 12;
  doc.setFillColor(...fill);
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.2);
  doc.roundedRect(x, y, width, height, 1, 1, 'FD');

  doc.setFontSize(6.5);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(75, 85, 99);
  doc.text(label, x + width / 2, y + 4.2, { align: 'center' });

  doc.setFontSize(9);
  doc.setTextColor(...textColor);
  doc.text(value, x + width / 2, y + 9, { align: 'center' });
}

function drawCompactMetadata(doc, summary, statusLabel, layout) {
  const startY = layout.headerEndY;
  const gap = 2;
  const tiles = [
    {
      label: 'Register Status',
      value: (statusLabel || 'draft').replace(/_/g, ' ').toUpperCase(),
      fill: [232, 236, 244],
      textColor: [27, 42, 74],
    },
    {
      label: 'Total Employees',
      value: String(summary.totals.headcount),
      fill: [249, 250, 251],
      textColor: [27, 42, 74],
    },
    {
      label: 'Already Paid',
      value: fmtCurrency(summary.already_paid_total),
      fill: [255, 251, 235],
      textColor: [180, 83, 9],
    },
    {
      label: 'Amount to Release',
      value: fmtCurrency(summary.current_release_total),
      fill: [240, 253, 244],
      textColor: [21, 128, 61],
    },
    {
      label: 'Register Total',
      value: fmtCurrency(summary.register_total),
      fill: [239, 246, 255],
      textColor: [29, 78, 216],
    },
  ];
  const tileWidth = (layout.contentWidth - (gap * (tiles.length - 1))) / tiles.length;
  tiles.forEach((tile, index) => {
    drawMetadataTile(doc, {
      ...tile,
      x: layout.leftX + index * (tileWidth + gap),
      y: startY,
      width: tileWidth,
    });
  });
  doc.setTextColor(0);
  return startY + 16;
}

/**
 * Render the approval cover section on the current PDF page.
 *
 * @param {Object} options Rendering inputs.
 * @returns {Promise<void>}
 */
export async function drawPayrollRegisterCoverPage({
  doc,
  autoTableFn,
  layout,
  summary,
  registerTitle,
  periodLabel,
  statusLabel,
}) {
  let y = drawCompactMetadata(doc, summary, statusLabel, layout);
  drawSeparatorLine(doc, y, layout.leftX, layout.rightX);
  y += 4;

  const tableData = buildCoverTableData(summary);
  const columnStyles = {};
  summary.columns.forEach((column, index) => {
    columnStyles[index] = {
      halign: column.type === 'text' ? ALIGN.TEXT : ALIGN.CURRENCY,
    };
    if (column.type === 'integer') columnStyles[index].halign = ALIGN.NUMBER;
    if (column.key === 'sn') columnStyles[index].cellWidth = 6;
    if (column.key === 'group') columnStyles[index].cellWidth = 27;
    if (column.key === 'headcount') columnStyles[index].cellWidth = 11;
  });

  const isDetailedCover = summary.columns.length > 12;
  const fontSize = summary.columns.length > 24 ? 4.2 : 5.5;
  autoTableFn(doc, {
    startY: y,
    head: tableData.head,
    body: tableData.body,
    ...DEFAULT_TABLE_STYLES,
    styles: {
      ...DEFAULT_TABLE_STYLES.styles,
      fontSize: isDetailedCover ? fontSize : 6.5,
      cellPadding: isDetailedCover ? 0.55 : 1.2,
      overflow: 'linebreak',
    },
    headStyles: {
      ...DEFAULT_TABLE_STYLES.headStyles,
      fontSize: isDetailedCover ? fontSize : 6.5,
      cellPadding: isDetailedCover ? 0.55 : 1.2,
    },
    margin: { ...layout.tableMargin, bottom: 58 },
    columnStyles,
    showHead: 'everyPage',
    didParseCell: (data) => {
      const column = summary.columns[data.column.index];
      const bandStyle = COVER_BAND_STYLES[column?.band];
      if (data.section === 'head') {
        data.cell.styles.fillColor = bandStyle?.header || [27, 42, 74];
        return;
      }
      if (data.section !== 'body') return;

      if (data.row.index === tableData.totalRowIndex) {
        data.cell.styles.fillColor = bandStyle?.total || [27, 42, 74];
        data.cell.styles.textColor = 255;
        data.cell.styles.fontStyle = 'bold';
      } else if (['sn', 'group', 'headcount'].includes(column?.key)) {
        data.cell.styles.fillColor = [255, 255, 255];
      } else {
        data.cell.styles.fillColor = bandStyle?.body || [226, 239, 218];
        data.cell.styles.textColor = [27, 42, 74];
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  let approvalY = (doc.lastAutoTable?.finalY || y + 20) + 6;
  const requiredHeight = 54;
  const pageHeight = doc.internal.pageSize.getHeight();
  if (approvalY + requiredHeight > pageHeight - 12) {
    doc.addPage('landscape');
    const headerEndY = await drawBrandedHeader(doc, {
      title: `${registerTitle} — Authorisation`,
      subtitle: `Period: ${periodLabel}`,
    });
    approvalY = headerEndY + 2;
  }

  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(27, 42, 74);
  const approvalText = (
    'Approval scope: signatures below approve the cover totals and all '
    + 'supporting schedules in this report.'
  );
  doc.text(approvalText, layout.leftX, approvalY);
  drawSignatures(doc, layout, approvalY + 6);
}
